
import { fromJS, List, Map } from 'immutable';
import { types as keywordsTypes } from 'redux/keywords/reducer';
import { types as productTypes } from 'redux/product/reducer';
import { createSymbols } from 'utils/helpers';
import { mapProduct } from 'utils/mapToRecord';
import { collectionReducer } from 'utils/reducerGenerator';

export const {
    ADD_PINNED_PRODUCT,
    ADD_PINNED_PRODUCT_DASHBOARD,
    FETCH_FILTERED_PRODUCTS,
    FETCH_FILTERED_PRODUCTS_FAIL,
    FETCH_FILTERED_PRODUCTS_SUCCESS,
    FETCH_NORMALIZED_PRODUCTS,
    FETCH_NORMALIZED_PRODUCTS_FAIL,
    FETCH_NORMALIZED_PRODUCTS_SUCCESS,
    REMOVE_PINNED_PRODUCT,
    REMOVE_PINNED_PRODUCT_DASHBOARD,
    REMOVE_TRACKED_PRODUCT,
} = createSymbols([
    'ADD_PINNED_PRODUCT',
    'ADD_PINNED_PRODUCT_DASHBOARD',
    'FETCH_FILTERED_PRODUCTS',
    'FETCH_FILTERED_PRODUCTS_FAIL',
    'FETCH_FILTERED_PRODUCTS_SUCCESS',
    'FETCH_NORMALIZED_PRODUCTS',
    'FETCH_NORMALIZED_PRODUCTS_FAIL',
    'FETCH_NORMALIZED_PRODUCTS_SUCCESS',
    'REMOVE_PINNED_PRODUCT',
    'REMOVE_PINNED_PRODUCT_DASHBOARD',
    'REMOVE_TRACKED_PRODUCT',
]);

const addKeywordToMatchingProduct = (keyword, products) => {
    const productId = keyword.get('productId');
    const matchingProduct = products.get(`${productId}`, Map({}));

    if (matchingProduct) {
        const matchingProductKeywords = matchingProduct.get('keywords');
        const updatedProducts = products.setIn(
            [`${productId}`, 'keywords'],
            matchingProductKeywords.push(keyword)
        );

        return updatedProducts;
    }

    return products;
};

const addKeywordToProduct = (state, { keyword }) => {
    const updatedProducts = addKeywordToMatchingProduct(keyword, state.get('normalizedData'));

    return state.set('normalizedData', updatedProducts);
};

const postKeywordSuccess = (state, { payload }) => {
    const data = fromJS(payload.data);
    const product = mapProduct(payload.data.product);
    const products = state.get('normalizedData', Map({}));
    const productId = product.get('id');
    const matchingProduct = products.get(`${productId}`);
    const newProduct = product.set('keywords', List([data.delete('product')]));

    if (matchingProduct) {
        return state;
    }


    return state.setIn(['normalizedData', `${productId}`], newProduct).setIn(
        ['keywordProducts', 'ids'],
        state.getIn(['keywordProducts', 'ids'], List([])).unshift(productId)
    );
};

const addPinnedProduct = (state, { id }) => {
    return state.setIn(
        ['pinnedProducts', 'ids'],
        state.getIn(['pinnedProducts', 'ids'], List([])).push(id)
    );
};

const addPinnedProductDashboard = (state, { product }) => {
    return state.setIn(
        ['pinnedProducts', 'data'],
        state.getIn(['pinnedProducts', 'data'], List([])).push(product)
    );
};

const removePinnedProduct = (state, { id }) => {
    return state.setIn(
        ['pinnedProducts', 'ids'],
        state.getIn(['pinnedProducts', 'ids']).filter((pId) => pId !== id)
    );
};

const removePinnedProductDashboard = (state, { product }) => {
    return state.setIn(
        ['pinnedProducts', 'data'],
        state.getIn(['pinnedProducts', 'data']).filter((p) => p.get('id') !== product.get('id'))
    );
};


const removeTrackedProduct = (state, { id }) => {
    const stateWithoutTrackedProduct = state.setIn(
        ['keywordProducts', 'ids'],
        state.getIn(['keywordProducts', 'ids']).filter((pId) => pId !== id)
    );

    return removePinnedProduct(stateWithoutTrackedProduct, { id });
};

const updateProductSuccess = (state, { payload }) => {
    const emptyList = Map({});
    const product = mapProduct(payload.data);
    const updateProduct = (p) => {
        if (p.get('id') === product.get('id')) {
            return product;
        }

        return p;
    };
    const data = state.get('normalizedData', emptyList).map(updateProduct);

    return state.set('normalizedData', data);
};

const fetchNormalizedProducts = (state, { name }) => state.setIn([name, 'isFetching'], true);

const fetchNormalizedProductsFinish = (state, { name }) => {
    return state.setIn([name, 'isFetching'], false).setIn([name, 'hasFetched'], true);
};

const fetchNormalizedProductsSuccess = (state, { meta, payload }) => {
    const { name } = meta.previousAction;
    const formattedData = fromJS(payload.data);
    const ids = payload.data.map((product) => product.id);
    const normalized = {};
    formattedData.forEach((product) => {
        normalized[product.get('id')] = mapProduct(product);
    });
    const updatedState = state.set(
        name,
        fromJS({ ids, pagination: payload.pagination })
    );

    return fetchNormalizedProductsFinish(updatedState, { name });
};

const fetchFilteredProductsSuccess = (state, { meta, payload }) => {
    const { name } = meta.previousAction;

    const data = fromJS(payload.data);
    const formattedData = data.map((product) => mapProduct(product));
    const updatedState = state.set(name, fromJS({
        data: formattedData,
        pagination: payload.pagination
    }));

    return fetchNormalizedProductsFinish(updatedState, { name });
};

const fetchPinnedProductsSuccess = (state, { payload }) => state.withMutations(
    (prevState) => {
        let data = payload.data.data || payload.data;
        const transformedData = fromJS(data).map(mapProduct);

        if (!Array.isArray(data)) {
            data = [data];
        }

        prevState.set('hasFetched', true)
            .set('isFetching', false)
            .set('pinnedProducts', transformedData);
    }
);

const patchKeywordSuccess = (state, { payload }) => {
    return updateProductSuccess(state, { payload: { data: payload.data.product } });
};

export const { reducer, types } = collectionReducer(
    { namespace: 'PRODUCT', transformer: mapProduct },
    () => ({
        ADD_KEYWORD_TO_PRODUCT: addKeywordToProduct,
        [ADD_PINNED_PRODUCT]: addPinnedProduct,
        [ADD_PINNED_PRODUCT_DASHBOARD]: addPinnedProductDashboard,
        [FETCH_FILTERED_PRODUCTS]: fetchNormalizedProducts,
        [FETCH_FILTERED_PRODUCTS_FAIL]: fetchNormalizedProductsFinish,
        [FETCH_FILTERED_PRODUCTS_SUCCESS]: fetchFilteredProductsSuccess,
        [FETCH_NORMALIZED_PRODUCTS]: fetchNormalizedProducts,
        [FETCH_NORMALIZED_PRODUCTS_FAIL]: fetchNormalizedProductsFinish,
        [FETCH_NORMALIZED_PRODUCTS_SUCCESS]: fetchNormalizedProductsSuccess,
        FETCH_PINNED_PRODUCTS_SUCCESS: fetchPinnedProductsSuccess,
        [keywordsTypes.POST_SUCCESS]: postKeywordSuccess,
        PATCH_KEYWORD_SUCCESS: patchKeywordSuccess,
        [productTypes.PUT_SUCCESS]: updateProductSuccess,
        [REMOVE_PINNED_PRODUCT]: removePinnedProduct,
        [REMOVE_PINNED_PRODUCT_DASHBOARD]: removePinnedProductDashboard,
        [REMOVE_TRACKED_PRODUCT]: removeTrackedProduct,
    })
);

export default reducer;
